import { readAll } from './collections';
import { serviceCall } from './runtime';
import { apiTransport } from './api-client';
import { env } from '../config/env';
import type { AdminEmployee } from './directory';
import type { AdminAttendanceRecord, AdminLeaveRequest, AdminOvertimeRequest } from './seed';
import type { Asset, Candidate, PayrollRun } from '@micarahr/shared-types';

/**
 * Global search.
 *
 * Permission-aware by construction: the caller passes the permissions it holds,
 * and a category whose permission is missing is never *searched*, not merely
 * hidden from the results. A search box that finds a payroll period and then
 * refuses to open it has already leaked that the period exists.
 */

export type SearchCategory =
  | 'employees'
  | 'attendance'
  | 'leave'
  | 'overtime'
  | 'payroll'
  | 'candidates'
  | 'documents'
  | 'assets'
  | 'policies'
  | 'announcements';

export interface SearchResult {
  id: string;
  category: SearchCategory;
  categoryLabel: string;
  title: string;
  subtitle: string;
  href: string;
}

const CATEGORY_PERMISSION: Record<SearchCategory, string[]> = {
  employees: ['employee.view_all', 'team.view'],
  attendance: ['attendance.review', 'team.attendance.view'],
  leave: ['leave.approve_hr', 'approval.view'],
  overtime: ['overtime.approve_hr', 'approval.view'],
  payroll: ['payroll.run', 'payroll.approve'],
  candidates: ['recruitment.manage'],
  documents: ['document.manage'],
  assets: ['asset.manage'],
  // Searched only by the API composite below; the prototype never indexed
  // these registries, so no mock block reads the entries.
  policies: ['policy.publish'],
  announcements: ['announcement.publish'],
};

const mockSearchService = {
  async query(term: string, permissions: string[], limit = 6): Promise<SearchResult[]> {
    return serviceCall('searchService', () => {
      const needle = term.trim().toLowerCase();
      if (needle.length < 2) return [];

      const granted = new Set(permissions);
      const allowed = (category: SearchCategory) =>
        CATEGORY_PERMISSION[category].some((permission) => granted.has(permission));

      const results: SearchResult[] = [];
      const match = (...values: (string | null | undefined)[]) =>
        values.some((value) => value?.toLowerCase().includes(needle));

      if (allowed('employees')) {
        for (const employee of readAll<AdminEmployee>('employees')) {
          if (
            match(
              employee.fullName,
              employee.employeeNumber,
              employee.workEmail,
              employee.positionTitle,
            )
          ) {
            results.push({
              id: employee.id,
              category: 'employees',
              categoryLabel: 'Employees',
              title: employee.fullName,
              subtitle: `${employee.employeeNumber} · ${employee.positionTitle}`,
              href: `/employees?employee=${employee.id}`,
            });
            if (results.length > limit * 4) break;
          }
        }
      }

      if (allowed('leave')) {
        for (const request of readAll<AdminLeaveRequest>('leaveRequests')) {
          if (match(request.referenceCode, request.employeeName, request.leaveTypeName)) {
            results.push({
              id: request.id,
              category: 'leave',
              categoryLabel: 'Leave',
              title: `${request.referenceCode} — ${request.leaveTypeName}`,
              subtitle: `${request.employeeName} · ${request.startDate}`,
              href: `/leave?request=${request.id}`,
            });
          }
        }
      }

      if (allowed('overtime')) {
        for (const request of readAll<AdminOvertimeRequest>('overtimeRequests')) {
          if (match(request.referenceCode, request.employeeName)) {
            results.push({
              id: request.id,
              category: 'overtime',
              categoryLabel: 'Overtime',
              title: `${request.referenceCode} — ${request.estimatedHours} h`,
              subtitle: `${request.employeeName} · ${request.date}`,
              href: `/overtime?request=${request.id}`,
            });
          }
        }
      }

      if (allowed('attendance')) {
        for (const record of readAll<AdminAttendanceRecord>('attendanceRecords').slice(0, 400)) {
          if (match(record.employeeName, record.date)) {
            results.push({
              id: record.id,
              category: 'attendance',
              categoryLabel: 'Attendance',
              title: `${record.employeeName} — ${record.date}`,
              subtitle: `${record.shiftName} · ${record.status.replace(/_/g, ' ')}`,
              href: `/attendance?record=${record.id}`,
            });
          }
        }
      }

      if (allowed('payroll')) {
        for (const run of readAll<PayrollRun>('payrollRuns')) {
          if (match(run.label, run.payrollGroupName)) {
            results.push({
              id: run.id,
              category: 'payroll',
              categoryLabel: 'Payroll',
              title: run.label,
              subtitle: `${run.payrollGroupName} · ${run.status.replace(/_/g, ' ')}`,
              href: `/payroll?run=${run.id}`,
            });
          }
        }
      }

      if (allowed('candidates')) {
        for (const candidate of readAll<Candidate>('candidates')) {
          if (match(candidate.fullName, candidate.referenceCode, candidate.requisitionTitle)) {
            results.push({
              id: candidate.id,
              category: 'candidates',
              categoryLabel: 'Candidates',
              title: candidate.fullName,
              subtitle: `${candidate.requisitionTitle} · ${candidate.stage.replace(/_/g, ' ')}`,
              href: `/recruitment/candidates?candidate=${candidate.id}`,
            });
          }
        }
      }

      if (allowed('assets')) {
        for (const asset of readAll<Asset>('assets')) {
          if (match(asset.name, asset.assetCode, asset.serialNumber, asset.assignedEmployeeName)) {
            results.push({
              id: asset.id,
              category: 'assets',
              categoryLabel: 'Assets',
              title: `${asset.assetCode} — ${asset.name}`,
              subtitle: asset.assignedEmployeeName ?? asset.status,
              href: `/assets?asset=${asset.id}`,
            });
          }
        }
      }

      // Interleave categories so one large category cannot crowd out the rest.
      const byCategory = new Map<SearchCategory, SearchResult[]>();
      for (const result of results) {
        const bucket = byCategory.get(result.category) ?? [];
        if (bucket.length < limit) bucket.push(result);
        byCategory.set(result.category, bucket);
      }
      return [...byCategory.values()].flat();
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Search against the API                                                      */
/* -------------------------------------------------------------------------- */

/*
 * The API has no search endpoint, so this is a client-side composite: a
 * fan-out to the list endpoints the caller may read, filtered where the server
 * cannot.
 *
 * Employees are the one category with a real server-side filter —
 * GET /employees?search= matches name, employee number and work email in the
 * database, inside the caller's scope. (Position title is not matched: the
 * server does not index it, and sending a parameter it strips would only
 * pretend otherwise.) The registries — announcements, policies, documents,
 * assets — have list endpoints with no text filter, so their (bounded) lists
 * are fetched and matched here.
 *
 * Permission-aware the same way as the prototype: a category the caller
 * cannot open is never requested, and a category the server refuses anyway
 * answers empty rather than failing the whole palette.
 */

/** Server permission keys, which is what an API session carries. */
const API_CATEGORY_PERMISSION: Partial<Record<SearchCategory, string[]>> = {
  employees: ['employees.view'],
  documents: ['document.manage'],
  assets: ['asset.manage'],
  // Announcements and policies answer to any signed-in account; the server
  // already scopes drafts to publishers.
};

interface ApiDirectoryHit {
  id: string;
  fullName: string;
  employeeNumber: string;
  position: { id: string; title: string } | null;
}

interface ApiAnnouncementHit {
  id: string;
  title: string;
  category: string | null;
  status: string;
}

interface ApiPolicyHit {
  id: string;
  title: string;
  category: string | null;
  summary: string | null;
  status: string;
}

interface ApiDocumentHit {
  id: string;
  name: string;
  documentType: string;
  employee: { id: string; fullName: string } | null;
}

interface ApiAssetHit {
  id: string;
  assetCode: string;
  name: string;
  serialNumber: string | null;
  status: string;
  assignedEmployeeName: string | null;
}

const apiSearchService = {
  async query(term: string, permissions: string[], limit = 6): Promise<SearchResult[]> {
    const trimmed = term.trim();
    const needle = trimmed.toLowerCase();
    if (needle.length < 2) return [];

    const granted = new Set(permissions);
    const allowed = (category: SearchCategory) => {
      const required = API_CATEGORY_PERMISSION[category];
      return !required || required.some((permission) => granted.has(permission));
    };

    const match = (...values: (string | null | undefined)[]) =>
      values.some((value) => value?.toLowerCase().includes(needle));

    // One category failing — a revoked permission racing the palette, a
    // deploy mid-search — must not blank the categories that answered.
    const safe = <T>(request: Promise<T[]>): Promise<T[]> => request.catch(() => []);
    const none = <T>(): Promise<T[]> => Promise.resolve([]);

    const [employees, announcements, policies, documents, assets] = await Promise.all([
      allowed('employees')
        ? safe(
            apiTransport()
              .page<ApiDirectoryHit>('/api/v1/employees', {
                query: { search: trimmed, limit },
              })
              .then((page) => page.items),
          )
        : none<ApiDirectoryHit>(),
      safe(apiTransport().get<ApiAnnouncementHit[]>('/api/v1/announcements')),
      safe(apiTransport().get<ApiPolicyHit[]>('/api/v1/policies')),
      allowed('documents')
        ? safe(apiTransport().get<ApiDocumentHit[]>('/api/v1/documents'))
        : none<ApiDocumentHit>(),
      allowed('assets')
        ? safe(apiTransport().get<ApiAssetHit[]>('/api/v1/assets'))
        : none<ApiAssetHit>(),
    ]);

    const results: SearchResult[] = [];

    for (const employee of employees.slice(0, limit)) {
      results.push({
        id: employee.id,
        category: 'employees',
        categoryLabel: 'Employees',
        title: employee.fullName,
        subtitle: `${employee.employeeNumber} · ${employee.position?.title ?? ''}`,
        href: `/employees?employee=${employee.id}`,
      });
    }

    for (const announcement of announcements
      .filter((row) => match(row.title, row.category))
      .slice(0, limit)) {
      results.push({
        id: announcement.id,
        category: 'announcements',
        categoryLabel: 'Announcements',
        title: announcement.title,
        subtitle: `${announcement.category ?? ''} · ${announcement.status.replace(/_/g, ' ')}`,
        href: `/announcements?announcement=${announcement.id}`,
      });
    }

    for (const policy of policies
      .filter((row) => match(row.title, row.category, row.summary))
      .slice(0, limit)) {
      results.push({
        id: policy.id,
        category: 'policies',
        categoryLabel: 'Policies',
        title: policy.title,
        subtitle: `${policy.category ?? ''} · ${policy.status.replace(/_/g, ' ')}`,
        href: `/policies?policy=${policy.id}`,
      });
    }

    for (const document of documents
      .filter((row) => match(row.name, row.documentType, row.employee?.fullName))
      .slice(0, limit)) {
      results.push({
        id: document.id,
        category: 'documents',
        categoryLabel: 'Documents',
        title: document.name,
        subtitle: `${document.employee?.fullName ?? ''} · ${document.documentType}`,
        href: `/documents?document=${document.id}`,
      });
    }

    for (const asset of assets
      .filter((row) => match(row.assetCode, row.name, row.serialNumber, row.assignedEmployeeName))
      .slice(0, limit)) {
      results.push({
        id: asset.id,
        category: 'assets',
        categoryLabel: 'Assets',
        title: `${asset.assetCode} — ${asset.name}`,
        subtitle: asset.assignedEmployeeName ?? asset.status,
        href: `/assets?asset=${asset.id}`,
      });
    }

    return results;
  },
};

export const searchService: typeof mockSearchService =
  env().dataSource === 'api' ? apiSearchService : mockSearchService;
